// Read-only display of the Management Guideline linked to a given (item, grade).
//
// SHCT-394 — the guideline↔item link is owned by the Management Guideline side
// (Req Clarification No 45 Option A / No 47 Option A; PRD 04 v1.5 §3.3): a
// guideline carries its one bound PRO leaf item + `grade` (G1/G2/G3). The PRO
// item's own `gradeGuidelines` GID map is NOT the source of truth and is never
// synced when a guideline is created, so we resolve the linkage from the
// guideline list, not from the item map. Unlinked grades render
// "연결된 관리지침 없음".
//
// SHCT-220 — the match is on the item's ID, not its name. Item names are not
// unique: uniqueness holds over the whole 영역/하위영역/항목/등급 tuple, so two
// items sharing a name in different sub-areas were indistinguishable and the
// old `linkedItem === itemName` lookup resolved BOTH to whichever guideline came
// first in the list — one of the two grade cards then showed the other item's
// guideline. The backend added `linkedItemId` for exactly this join.

import { useTranslation } from 'react-i18next';

import type {
  GuidelineGrade,
  ManagementGuideline,
} from '@/features/management-guidelines/types/management-guidelines.types';
import type { GradeKey } from '../types/pro-item-design.types';

// PRD §3.3 — the PRO grade keys (g1/g2/g3) map 1:1 onto the guideline grade
// codes (G1/G2/G3). Kept as an explicit table so the mapping is unambiguous.
export const GRADE_KEY_TO_GUIDELINE_GRADE: Record<GradeKey, GuidelineGrade> = {
  g1: 'G1',
  g2: 'G2',
  g3: 'G3',
};

// Finds the guideline linked to (itemId, grade). 1:1 per PRD §3.3, so the first
// match is authoritative; returns null when the grade is unlinked.
//
// A guideline whose `linkedItemId` is null is bound to nothing and must never
// match — including against a draft item, whose id is negative (draft-node.ts
// hands out negative ids; real server ids are always positive), so a draft can
// never collide with a real row either.
export function findLinkedGuideline(
  guidelines: readonly ManagementGuideline[],
  itemId: number,
  grade: GradeKey,
): ManagementGuideline | null {
  const guidelineGrade = GRADE_KEY_TO_GUIDELINE_GRADE[grade];
  return (
    guidelines.find(
      (g) => g.linkedItemId !== null && g.linkedItemId === itemId && g.grade === guidelineGrade,
    ) ?? null
  );
}

export function GuidelineReadonly({
  grade,
  itemId,
  guidelines,
}: {
  grade: GradeKey;
  itemId: number;
  guidelines: readonly ManagementGuideline[];
}) {
  const { t } = useTranslation('pro-item-design');
  // #5 — truncate a long linked-guideline name (with a hover title) so it can't
  // blow out the Grade card head row.
  const ellipsis: React.CSSProperties = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
  const linked = findLinkedGuideline(guidelines, itemId, grade);
  if (!linked) {
    return (
      <span
        style={{
          fontSize: 11,
          fontStyle: 'italic',
          color: 'var(--muted-foreground)',
          ...ellipsis,
        }}
      >
        {t('item.fields.gradeCutoff.guideline.none')}
      </span>
    );
  }
  const label = `${linked.gid} ${linked.name}`;
  return (
    <span
      style={{ fontSize: 11, fontWeight: 600, color: 'var(--foreground)', ...ellipsis }}
      title={label}
    >
      {label}
    </span>
  );
}
